import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { ProfilesCrumbs } from '../constants';
import FiltersProfiles from 'system/components/profiles/list/filters';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { useFormik } from 'formik';
import Table from 'shell/components/shared/table';
import { Profile as ProfileVM, FiltersFormProfiles } from './types';
import { generatePath, useNavigate } from 'react-router-dom';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import { HttpResponseList } from 'shell/models/http-response';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-old-http-fetch';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import { transformDataset } from './utils';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { Profile } from 'shell/models/profile';
import { useActionAuthorization } from 'shell/hooks/use-action-authorization';
import { authsObj, validationSchemaProfilesFilter, headCells } from './constants';
import ListHeader from 'shell/components/shared/list-header';
import { Typography } from '@mui/material';

const initialValues: FiltersFormProfiles = {
  id: '',
  description: '',
  orders: {},
  page: 0,
  rowsPerPage: 10,
};

const ProfilesPage = (): React.ReactElement => {
  const navigate = useNavigate();

  const [deleteAlertModal, setDeleteAlertModal] = useState<AlertModal<ProfileVM>>({
    element: null,
    open: false,
  });

  const [deleteSuccessModal, setDeleteSuccessModal] = useState<AlertModal<ProfileVM>>({
    element: null,
    open: false,
  });

  const { delete: deleteProfile } = useHttpFetch(`${__MANAGEMENT_API_URL__}/profili`);

  const {
    data,
    get: getProfiles,
    error,
  } = useHttpFetch<HttpResponseList<Profile>>(`${__MANAGEMENT_API_URL__}/profili/`, {
    data: {
      payload: {
        pageInfo: {
          totalCount: 0,
        },
        list: [],
      },
    },
  });

  const profiles: Array<ProfileVM> = useMemo(() => {
    if (!!error || !data?.payload) {
      return [];
    }
    return data.payload.list.map((profile) => ({
      id: profile.key.codice.toString(),
      description: profile.nome,
    }));
  }, [data, error]);

  const count: number = useMemo(() => {
    if (!!error || !data?.payload || !data?.payload.list) {
      return 0;
    }
    return data.payload.list.length;
  }, [error, data?.payload]);

  const onSubmit = useCallback(
    (formValues: FiltersFormProfiles) => {
      const queryString = transformDataset({ ...formValues, page: 0 });
      filterProfiles.setFieldValue('page', 0);
      getProfiles(queryString);
    },
    [getProfiles]
  );

  const filterProfiles = useFormik({
    initialValues,
    validationSchema: validationSchemaProfilesFilter,
    onSubmit,
  });

  const filterTable = useFilterTable<FiltersFormProfiles>();

  useEffect(() => {
    filterProfiles.setValues(filterTable.values || initialValues);
    const queryString = transformDataset(filterTable.values || initialValues);
    getProfiles(queryString);
  }, []);

  useEffect(() => {
    return () => filterTable.setValues(filterProfiles.values);
  }, [filterProfiles.values]);

  const auths = useActionAuthorization(authsObj);

  const onVisibilityPress = useCallback(
    (cell): Promise<ProfileVM> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/profili/dettaglio/:id', { id: cell.id.toString() }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onEditPress = useCallback(
    (cell): Promise<ProfileVM> => {
      return new Promise((resolve) => {
        navigate(generatePath('/sistema/profili/modifica/:id', { id: cell.id.toString() }));
        resolve(cell);
      });
    },
    [navigate]
  );

  const onTrashPress = useCallback(
    (cell): Promise<ProfileVM> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<ProfileVM>;
    },
    [setDeleteAlertModal]
  );

  const handleChangePage = useCallback(
    (newPage) => {
      filterProfiles.setFieldValue('page', newPage);
    },
    [filterProfiles.setFieldValue]
  );

  const handleChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = { ...filterProfiles.values, rowsPerPage: rowsPerPage || 0, page: 0 };
      filterProfiles.setValues(newDataset);
    },
    [filterProfiles.setFieldValue, filterProfiles.values]
  );

  const onSort = useCallback(
    (orders) => {
      const newDataset = { ...filterProfiles.values, orders };

      const queryString = transformDataset(newDataset);
      return getProfiles(queryString).then(() => {
        filterProfiles.setFieldValue('orders', orders);
      });
    },
    [filterProfiles.setFieldValue, getProfiles, filterProfiles.values]
  );

  return (
    <Fragment>
      <Breadcrumbs items={ProfilesCrumbs} />
      <ListHeader
        addBtnAriaLabel="Aggiungi nuovo profilo"
        addBtnLabel="Aggiungi nuovo profilo"
        canAdd={auths.create}
        onAddBtnClick={() => {
          navigate('/sistema/profili/nuovo');
        }}
        title="Gestione Profili"
      />
      <div className="pmi-my-3">
        <FiltersProfiles filterProfiles={filterProfiles} />
      </div>
      {data?.payload ? (
        profiles?.length > 0 ? (
          <Table
            onSort={onSort}
            head={headCells}
            orders={filterProfiles.values.orders}
            activeActions={auths}
            page={filterProfiles.values.page || 0}
            rowsPerPage={filterProfiles.values.rowsPerPage}
            body={profiles}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            count={count || 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ariaLabel="Lista Profili"
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione profilo"
        message={`Confermare la cancellazione del profilo ${deleteAlertModal.element?.description} selezionato?`}
        open={deleteAlertModal.open}
        onClose={() => setDeleteAlertModal({ open: false, element: null })}
        onNo={() => setDeleteAlertModal({ open: false, element: null })}
        onYes={() => {
          deleteProfile(`/${deleteAlertModal.element?.id}`).then(() => {
            setDeleteSuccessModal({ open: true, element: deleteAlertModal.element });
            setDeleteAlertModal({ open: false, element: null });
          });
        }}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione profilo"
        message={`La cancellazione del profilo ${deleteSuccessModal.element?.description} è avvenuta con successo.`}
        onClose={() => {
          const queryString = transformDataset({ ...filterProfiles.values, page: 0 });
          getProfiles(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
        onOk={() => {
          const queryString = transformDataset({ ...filterProfiles.values, page: 0 });
          getProfiles(queryString).then(() =>
            setDeleteSuccessModal({ open: false, element: null })
          );
        }}
      />
    </Fragment>
  );
};

ProfilesPage.displayName = 'Profiles list page component';

export default ProfilesPage;
